import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ToastType = "success" | "error" | "info" | "warning";

type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

type Toast = {
  id: string;
  title: string;
  description?: string;
  type?: ToastType;
};

type ToastContextValue = {
  toast: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
};

type ToastProviderProps = {
  children: React.ReactNode;
  position?: ToastPosition;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_LIMIT = 3;
const TOAST_DURATION = 4000;

const positionClasses: Record<ToastPosition, string> = {
  "top-left": "top-6 left-6 items-start",
  "top-center": "top-6 left-1/2 -translate-x-1/2 items-center",
  "top-right": "top-6 right-6 items-end",

  "bottom-left": "bottom-6 left-6 items-start",
  "bottom-center": "bottom-6 left-1/2 -translate-x-1/2 items-center",
  "bottom-right": "bottom-6 right-6 items-end",
};

export function ToastProvider({
  children,
  position = "bottom-center",
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();

      setToasts((current) =>
        [
          {
            type: "info" as ToastType,
            ...toast,
            id,
          },
          ...current,
        ].slice(0, TOAST_LIMIT),
      );

      window.setTimeout(() => dismiss(id), TOAST_DURATION);
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      toast,
      dismiss,
    }),
    [toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div
        className={[
          "pointer-events-none fixed z-50 flex w-full max-w-md flex-col px-4",
          positionClasses[position],
        ].join(" ")}
      >
        <div className="relative h-[140px] w-full">
          {toasts.map((toast, index) => (
            <ToastItem
              key={toast.id}
              toast={toast}
              index={index}
              onDismiss={dismiss}
            />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  index,
  onDismiss,
}: {
  toast: Toast;
  index: number;
  onDismiss: (id: string) => void;
}) {

  const variants: Record<ToastType, string> = {
    success:
      "border-emerald-200/80 bg-emerald-50/90 text-emerald-950 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-100",

    error:
      "border-red-200/80 bg-red-50/90 text-red-950 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-100",

    warning:
      "border-amber-200/80 bg-amber-50/90 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100",

    info:
      "border-sky-200/80 bg-sky-50/90 text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-100",
  };

  return (
    <div
      className={[
        "pointer-events-auto absolute inset-x-0 bottom-0 rounded-2xl px-5 py-4",
        "shadow-[0_12px_40px_rgba(15,23,42,0.14)]",
        "backdrop-blur-xl",
        "transition-all duration-300 ease-out",
        variants[toast.type ?? "info"],
      ].join(" ")}
      data-toast-index={Math.min(index, 2)}
    >
      <div className="animate-toast-in">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-heading text-[15px] font-semibold tracking-tight">
              {toast.title}
            </p>

            {toast.description && (
              <p className="mt-1 text-sm leading-5 opacity-80">
                {toast.description}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            className="shrink-0 rounded-md opacity-60 transition hover:opacity-100"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used inside ToastProvider");
  }

  return context;
}